import { Box, Group, rem, Stack, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";
import Badge from "@/design-system/components/Badge";

interface TabSectionHeaderProps {
  icon?: ReactNode;
  title: string;
  /** Rótulo curto ao lado do título ("A IA CONSULTA", "A IA ENVIA"). */
  badge?: string;
  description: ReactNode;
  /** Linha própria abaixo da descrição (ex.: aviso de pendências da galeria). */
  summary?: ReactNode;
  /** Ação da aba, à direita do título (ex.: o botão de salvar desta aba). */
  action?: ReactNode;
}

/**
 * Cabeçalho das abas do agente (Cérebro, Galeria, Integrações): título com
 * ícone e badge, mais uma descrição curta do que a aba faz. Componente de
 * feature, não de DS: só a página do agente usa esta composição.
 */
export function TabSectionHeader({
  icon,
  title,
  badge,
  description,
  summary,
  action,
}: TabSectionHeaderProps) {
  // No celular a ação não cabe ao lado do título; em vez de quebrar entre o
  // título e a descrição, vai depois dela: título, texto, botão.
  const { isMobileScreen } = useMobileScreen();
  const actionInTitleRow = action && !isMobileScreen;
  return (
    <Stack gap={rem(6)}>
      {/* Altura mínima = a do botão de salvar: assim o título fica na mesma
          posição nas abas com e sem ação, e a descrição também. */}
      <Group
        justify="space-between"
        align="center"
        gap={rem(12)}
        mih={rem(36)}>
        <Group
          gap={rem(8)}
          wrap="nowrap">
          {icon}
          <Title
            order={2}
            fw={700}
            ff="heading"
            fz="lg"
            c="plain_text">
            {title}
          </Title>
          {badge && (
            <Badge
              variant="brand"
              size="sm"
              style={{ cursor: "default", letterSpacing: rem(0.4) }}>
              {badge}
            </Badge>
          )}
        </Group>
        {actionInTitleRow && action}
      </Group>
      {/* 14px em vez de 12px: a descrição é o que explica a aba e sumia em
          tela densa. Peso 500 para o cinza não ficar leve demais. */}
      <Text
        fz="md"
        fw={500}
        c="dimmed"
        maw={rem(680)}
        style={{ textWrap: "pretty" }}>
        {description}
      </Text>
      {summary}
      {/* Grid de uma coluna: o botão ocupa a largura toda, como os demais no celular. */}
      {action && !actionInTitleRow && (
        <Box style={{ display: "grid" }}>{action}</Box>
      )}
    </Stack>
  );
}

export default TabSectionHeader;
